import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { type InfringementListQuery } from '@/shared/graphql/router/graphql'
import { useUserStore } from '@/shared/stores/user'
import { closeAllDialogs, openDialog, Dialog } from '@/shared/dialogs/dialog'
import { closeAllModals } from '@/shared/dialogs/modals'
import { closeAllSheets } from '@/shared/dialogs/sheets'
import { applyAuthorizationToken } from '@/shared/utils/url'

import { findingPanel } from '../match/levels'
import type { MatchSide } from '../match/MatchCompareCard.vue'
import { useCreateFlaggedReport } from '../match/useCreateFlaggedReport'
import { useInfringingFileDetails } from '../match/useInfringingFileDetails'

export type FlaggedInfringement =
  InfringementListQuery['infringements']['edges'][number]['node']

export type FlaggedStep = 'match' | 'confirm' | 'details'

const TITLES: Record<FlaggedStep, string> = {
  match: 'Potential match found',
  confirm: 'Confirm report creation',
  details: 'Potential infringing work',
}

const thumb = (url?: string | null) =>
  applyAuthorizationToken(url, [{ key: 'width', value: 72 }])

export const useFlaggedMatch = (infringement: FlaggedInfringement) => {
  const router = useRouter()
  const userStore = useUserStore()
  const step = ref<FlaggedStep>('match')
  const { create, loading: creating, error } = useCreateFlaggedReport()
  const { file: details } = useInfringingFileDetails(infringement.id)

  const copyright = infringement.copyrights[0]
  const infringingFile = infringement.infringingFile

  const works = computed<{ registered: MatchSide; infringing: MatchSide }>(
    () => ({
      registered: {
        name: copyright?.name ?? '',
        type: copyright?.type,
        src: thumb(copyright?.asset?.previewImageUrl),
      },
      infringing: {
        name: infringingFile.name,
        type: infringingFile.type,
        src: thumb(infringingFile.asset?.previewImageUrl),
      },
    })
  )

  const panel = computed(() => findingPanel(infringement))

  const local = computed(() => ({
    name: infringingFile.name,
    type: infringingFile.type,
    previewUrl: thumb(infringingFile.asset?.previewImageUrl),
  }))

  const title = computed(() => TITLES[step.value])
  const credits = computed(() => userStore.user?.reportCredits)

  const openFile = (copyrightId: string) => {
    closeAllModals()
    closeAllSheets()
    router.push(`/file/${copyrightId}`)
  }

  const registrationId = copyright?.copyright?.id
  const viewRegistered = registrationId
    ? () => openFile(registrationId)
    : undefined

  const handleCreate = () => {
    if (credits.value === undefined) return
    if (!credits.value) {
      openDialog(Dialog.BuyReportCredits, {
        props: {
          afterPurchase: () =>
            openDialog(Dialog.FlaggedMatch, { props: { infringement } }),
        },
      })
      return
    }
    error.value = null
    step.value = 'confirm'
  }

  const confirm = async () => {
    if (await create(infringement)) closeAllDialogs()
  }

  const back = () => {
    if (creating.value) return
    error.value = null
    step.value = 'match'
  }

  return {
    step,
    title,
    works,
    panel,
    details,
    local,
    credits,
    creating,
    error,
    viewRegistered,
    openDetails: () => (step.value = 'details'),
    openFile,
    handleCreate,
    confirm,
    back,
  }
}

export type FlaggedMatch = ReturnType<typeof useFlaggedMatch>
